import * as dateFns from 'date-fns';
import FocusTrap from 'focus-trap-react';
import React, { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { DayPicker, type DayPickerSingleProps } from 'react-day-picker';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import Calendar from '../Icons/Calendar';

import InputIcon from './InputIcon';

import type { InputProps } from './index';

import './input-date.module.css';

export const parseDate = (str: string, format: string): Date | undefined => {
    const parsed = dateFns.parse(str, format, new Date());
    if (dateFns.isDate(parsed)) {
        return parsed;
    }
    return undefined;
};

export const formatDate = (date: Date, format: string): string => {
    return dateFns.format(date, format);
};

export type InputDateProps = {
    name: string;
    label?: string;
    format?: string;
    parseDate: (str: string, format: string) => Date | undefined;
    formatDate: (date: Date, format: string) => string;
    inputProps?: Partial<InputProps>;
    className?: string;
    onDayChange?: (date: Date | undefined) => void;
} & Omit<DayPickerSingleProps, 'mode'>;

export default function InputDate({
    className,
    format = 'dd/MM/yyyy',
    formatDate,
    label,
    name,
    parseDate,
    inputProps,
    onDayChange,
    ...props
}: InputDateProps) {
    const inputPropsValue = inputProps?.value ?? '';
    const [selected, setSelected] = useState<Date>();
    const [inputValue, setInputValue] = useState<InputProps['value']>(inputPropsValue);
    const [isPopperOpen, setIsPopperOpen] = useState(false);

    const popperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLInputElement>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

    const popper = usePopper(popperRef.current, popperElement, {
        placement: 'bottom-start',
    });

    const closePopper = () => {
        setIsPopperOpen(false);
        buttonRef?.current?.focus();
    };

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setInputValue(e.currentTarget.value);
        const date = parseDate(e.currentTarget.value, format);
        if (dateFns.isValid(date)) {
            setSelected(date);
        } else {
            setSelected(undefined);
        }
    };

    const handleButtonClick = () => {
        if (inputProps?.disabled) {
            return;
        }
        setIsPopperOpen(true);
    };

    const handleDaySelect = (day: Date | undefined) => {
        setSelected(day);
        if (day) {
            setInputValue(formatDate(day, format));
            closePopper();
        } else {
            setInputValue('');
        }
        if (onDayChange) {
            onDayChange(day);
        }
    };

    useEffect(() => {
        setInputValue(inputPropsValue);
    }, [inputPropsValue]);

    return (
        <div>
            <div ref={popperRef}>
                <InputIcon
                    {...inputProps}
                    className={className}
                    label={label ?? inputProps?.label}
                    name={name}
                    icon={<Calendar />}
                    value={inputValue}
                    onClick={handleButtonClick}
                    onChange={handleInputChange}
                    ref={buttonRef}
                />
            </div>
            {isPopperOpen &&
                createPortal(
                    <FocusTrap
                        active
                        focusTrapOptions={{
                            initialFocus: false,
                            allowOutsideClick: true,
                            clickOutsideDeactivates: true,
                            onDeactivate: closePopper,
                            fallbackFocus: buttonRef.current ?? undefined,
                        }}
                    >
                        <div
                            className="rdp-popper"
                            tabIndex={-1}
                            style={popper.styles.popper}
                            {...popper.attributes.popper}
                            ref={setPopperElement}
                            role="dialog"
                        >
                            <DayPicker
                                initialFocus={isPopperOpen}
                                mode="single"
                                defaultMonth={selected}
                                selected={selected}
                                onSelect={handleDaySelect}
                                {...props}
                            />
                        </div>
                    </FocusTrap>,
                    document.body
                )}
        </div>
    );
}
